import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, Menu, X, Calculator as CalcIcon } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { FEATURED_CALCULATORS, CATEGORIES } from '../data/mock';

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const navigate = useNavigate();

  const allItems = [
    ...FEATURED_CALCULATORS.map(c => ({ id: c.id, name: c.name, enabled: true })),
    ...CATEGORIES.flatMap(cat => cat.items.filter(i => i.disabled).map(i => ({ id: i.id, name: i.name, enabled: false })))
  ];
  const results = query.trim()
    ? allItems.filter(i => i.name.toLowerCase().includes(query.toLowerCase())).slice(0, 6)
    : [];

  const onSelect = (item) => {
    if (item.enabled) {
      navigate(`/calculator/${item.id}`);
      setQuery('');
      setSearchOpen(false);
      setMobileOpen(false);
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
      <div className="tricolor-bar" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-saffron-500 to-indgreen-500 flex items-center justify-center shadow-sm">
              <CalcIcon className="w-5 h-5 text-white" strokeWidth={2.5} />
            </div>
            <div className="flex flex-col leading-tight">
              <span className="font-display font-bold text-lg text-bharat-navy">
                bharat<span className="text-saffron-500">-</span>calculator
              </span>
              <span className="text-[10px] text-slate-500 -mt-1 tracking-wider">.COM • MADE IN INDIA</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {CATEGORIES.map(cat => (
              <div key={cat.name} className="relative group">
                <button className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-bharat-navy rounded-md hover:bg-slate-50 transition-colors">
                  {cat.name.replace(' Calculators', '').replace(' & Health', '')}
                </button>
                <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-lg shadow-xl border border-slate-100 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-150 z-50 p-2">
                  {cat.items.map(item => (
                    <button
                      key={item.id}
                      disabled={item.disabled}
                      onClick={() => !item.disabled && navigate(`/calculator/${item.id}`)}
                      className={`w-full text-left px-3 py-2 text-sm rounded-md flex items-center justify-between ${
                        item.disabled
                          ? 'text-slate-400 cursor-not-allowed'
                          : 'text-slate-700 hover:bg-saffron-50 hover:text-saffron-700'
                      }`}
                    >
                      <span>{item.name}</span>
                      {item.disabled && <span className="text-[10px] uppercase tracking-wider text-slate-400">Soon</span>}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </nav>

          {/* Search + mobile */}
          <div className="flex items-center gap-2">
            <div className="relative hidden sm:block">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search calculators…"
                value={query}
                onChange={(e) => { setQuery(e.target.value); setSearchOpen(true); }}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                className="pl-9 w-56 lg:w-72 h-9 bg-slate-50 border-slate-200 focus:bg-white"
              />
              {searchOpen && results.length > 0 && (
                <div className="absolute top-full right-0 mt-1 w-80 bg-white rounded-lg shadow-xl border border-slate-100 z-50 p-2">
                  {results.map(item => (
                    <button
                      key={item.id}
                      onMouseDown={() => onSelect(item)}
                      disabled={!item.enabled}
                      className={`w-full text-left px-3 py-2 text-sm rounded-md flex items-center justify-between ${
                        item.enabled ? 'hover:bg-saffron-50 text-slate-700' : 'text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      <span>{item.name}</span>
                      {!item.enabled && <span className="text-[10px] text-slate-400">Soon</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileOpen(!mobileOpen)}
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-slate-100 py-3 space-y-2 fade-in-up">
            <div className="relative px-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search calculators…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 bg-slate-50"
              />
            </div>
            {query ? (
              <div className="space-y-1">
                {results.map(item => (
                  <button
                    key={item.id}
                    onClick={() => onSelect(item)}
                    disabled={!item.enabled}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md ${
                      item.enabled ? 'hover:bg-saffron-50 text-slate-700' : 'text-slate-400'
                    }`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>
            ) : (
              CATEGORIES.map(cat => (
                <details key={cat.name} className="group">
                  <summary className="px-3 py-2 text-sm font-semibold text-bharat-navy cursor-pointer list-none flex items-center justify-between">
                    {cat.name}
                    <span className="text-slate-400 group-open:rotate-180 transition-transform">▾</span>
                  </summary>
                  <div className="pl-4 space-y-1">
                    {cat.items.map(item => (
                      <button
                        key={item.id}
                        onClick={() => !item.disabled && navigate(`/calculator/${item.id}`)}
                        disabled={item.disabled}
                        className={`w-full text-left px-3 py-1.5 text-sm rounded-md ${
                          item.disabled ? 'text-slate-400' : 'text-slate-700 hover:bg-saffron-50'
                        }`}
                      >
                        {item.name}{item.disabled && <span className="ml-2 text-[10px] text-slate-400">Soon</span>}
                      </button>
                    ))}
                  </div>
                </details>
              ))
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
